import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';
import { useAgentNotesDashboard } from '@apps/web/features/admin_agent_notes/model/use_agent_notes_dashboard';
import {
    DailyBySourceChart,
    DomainMixChart,
    FailureRateChart,
    HourOfDayChart,
    PipelineFunnel,
} from '@apps/web/features/admin_agent_notes/ui/dashboard_charts';
import { NotesHeatmap } from '@apps/web/features/admin_agent_notes/ui/notes_heatmap';
import { NotesKpiStrip } from '@apps/web/features/admin_agent_notes/ui/notes_kpi_strip';
import { OrgVolumeChart } from '@apps/web/features/admin_agent_notes/ui/org_volume_chart';
import { RangeControl } from '@apps/web/features/admin_agent_notes/ui/range_control';
import { StageLatencyCard } from '@apps/web/features/admin_agent_notes/ui/stage_latency_card';

function ChartCard({
    title,
    description,
    children,
    className,
}: {
    readonly title: string;
    readonly description?: string;
    readonly children: React.ReactNode;
    readonly className?: string;
}) {
    return (
        <Card className={className}>
            <CardHeader>
                <CardTitle className="text-sm">{title}</CardTitle>
                {description && (
                    <CardDescription>{description}</CardDescription>
                )}
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

export function DashboardTab() {
    const dashboard = useAgentNotesDashboard();
    const stats = dashboard.stats;

    return (
        <div className="flex flex-col gap-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            <div className="flex flex-wrap items-center gap-2">
                <AdminOrgPicker
                    value={dashboard.organizationId}
                    onValueChange={dashboard.setOrganization}
                />
                <RangeControl
                    preset={dashboard.rangePreset}
                    fixed={dashboard.fixedRange}
                    defaultPreset="14d"
                    onChange={dashboard.setRangeSelection}
                />
                <span className="text-muted-foreground ml-auto text-xs">
                    Times in {ANALYTICS_TIMEZONE}
                </span>
            </div>
            <NotesKpiStrip
                stats={stats}
                isLoading={dashboard.isLoading}
                buildNotesSearch={dashboard.buildNotesSearch}
            />
            {dashboard.hasError && (
                <p className="text-destructive rounded-md border px-3 py-6 text-center text-sm">
                    Stats failed to load. Refresh to try again.
                </p>
            )}
            {stats === undefined && !dashboard.hasError && (
                <Skeleton className="h-[180px]" />
            )}
            {stats && (
                <div className="flex flex-col gap-4">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <ChartCard
                            title="Notes per day by source"
                            description="Daily capture volume, stacked by source"
                        >
                            <DailyBySourceChart stats={stats} />
                        </ChartCard>
                        <ChartCard
                            title="Failure rate"
                            description="Share of each day's notes that failed"
                        >
                            <FailureRateChart stats={stats} />
                        </ChartCard>
                        <ChartCard
                            title="Notes by hour of day"
                            description="In each organization's local time"
                        >
                            <HourOfDayChart stats={stats} />
                        </ChartCard>
                        <ChartCard title="Pipeline conversion">
                            <PipelineFunnel stats={stats} />
                        </ChartCard>
                        <ChartCard
                            title="Stage latencies"
                            description="How long each pipeline hop takes"
                        >
                            <StageLatencyCard stats={stats} />
                        </ChartCard>
                        <ChartCard
                            title="Domain mix"
                            description="Routed items by destination domain"
                        >
                            <DomainMixChart stats={stats} />
                        </ChartCard>
                        {dashboard.organizationId === undefined && (
                            <ChartCard
                                title="Top 10 organizations by note volume"
                                description="Live orgs with the most notes in this range; click a bar to filter"
                            >
                                <OrgVolumeChart
                                    entries={stats.org_volume.top}
                                    emptyMessage="No live organizations captured notes in this range."
                                    onSelectOrganization={
                                        dashboard.setOrganization
                                    }
                                />
                            </ChartCard>
                        )}
                        {dashboard.organizationId === undefined && (
                            <ChartCard
                                title="Least active organizations"
                                description="Live orgs with the fewest notes, including none, in this range; click a bar to filter"
                            >
                                <OrgVolumeChart
                                    entries={stats.org_volume.least_active}
                                    emptyMessage="All live organizations fit in the top 10."
                                    onSelectOrganization={
                                        dashboard.setOrganization
                                    }
                                />
                            </ChartCard>
                        )}
                    </div>
                    {dashboard.heatmapStats && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm">
                                    Notes activity
                                </CardTitle>
                                <CardDescription>
                                    Daily volume this year; not affected by the
                                    date filter above
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <NotesHeatmap stats={dashboard.heatmapStats} />
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}
